import {
  type SelectionAiActionId,
  type SelectionInsightActionId,
} from '@/lib/selection-ai-prompts';
import {
  AlignLeft,
  BookCheck,
  MessageSquareQuote,
  ScrollText,
  Search,
  type LucideIcon,
} from 'lucide-react';
import { type MessageDescriptor } from 'react-intl';
import { selectionAiActionsMessages } from './selection-ai-actions.messages';

export interface SelectionActionItem {
  id: SelectionAiActionId;
  icon: LucideIcon;
  message: MessageDescriptor;
}

/**
 * Static action lists — regional filtering (e.g. `check_guidelines` for
 * non-US compliance) lives at the consumer via
 * `isSelectionCheckGuidelinesAllowed`. Do not assume every market sees
 * every row.
 */
export const V1_ACTIONS: SelectionActionItem[] = [
  {
    id: 'make_shorter',
    icon: AlignLeft,
    message: selectionAiActionsMessages.makeShorter,
  },
  {
    id: 'add_detail',
    icon: ScrollText,
    message: selectionAiActionsMessages.addMoreDetail,
  },
  {
    id: 'check_guidelines',
    icon: Search,
    message: selectionAiActionsMessages.checkWithGuidelines,
  },
];

export const V2_REWRITE_SUBMENU: SelectionActionItem[] = [
  {
    id: 'make_shorter',
    icon: AlignLeft,
    message: selectionAiActionsMessages.makeShorter,
  },
  {
    id: 'add_detail',
    icon: ScrollText,
    message: selectionAiActionsMessages.addMoreDetail,
  },
  {
    id: 'simplify_language',
    icon: BookCheck,
    message: selectionAiActionsMessages.simplifyLanguage,
  },
];

// View transcript (inline insight) first; Check evidence last, since it's the
// one that launches the chat panel. Consumers must filter `check_guidelines`
// with `isSelectionCheckGuidelinesAllowed` for non-US compliance.
export const V2_ACTIONS: SelectionActionItem[] = [
  {
    id: 'view_source',
    icon: MessageSquareQuote,
    message: selectionAiActionsMessages.viewSource,
  },
  {
    id: 'check_guidelines',
    icon: Search,
    message: selectionAiActionsMessages.checkWithGuidelines,
  },
];

/** Preset rows of the compact toolbar's Ask Heidi dropdown, in display order. */
export const COMPACT_MENU_ACTIONS: SelectionActionItem[] = [
  {
    id: 'view_source',
    icon: MessageSquareQuote,
    message: selectionAiActionsMessages.viewSource,
  },
  {
    id: 'check_guidelines',
    icon: Search,
    message: selectionAiActionsMessages.checkWithGuidelines,
  },
  {
    id: 'make_shorter',
    icon: AlignLeft,
    message: selectionAiActionsMessages.makeShorter,
  },
  {
    id: 'add_detail',
    icon: ScrollText,
    message: selectionAiActionsMessages.addMoreDetail,
  },
];

/** Actions that transform the text (vs answer questions about it) — these run
 * inline in the note when the v3/v4/v5 variants are active. */
export const INLINE_EDIT_ACTION_IDS: ReadonlySet<SelectionAiActionId> = new Set([
  'rewrite',
  'make_shorter',
  'add_detail',
  'simplify_language',
]);

/**
 * Informational actions that run inline as key-points insights in v5.
 *
 * `check_guidelines` is deliberately NOT here: the inline flow uses the
 * session-scoped `ask-ai` endpoint, whose context is only the session's
 * transcript/documents — it has no evidence retrieval, so guideline checks
 * always came back as refusals. It routes to the Evidence chat panel (full
 * retrieval stack) instead.
 */
export const INLINE_INSIGHT_ACTION_IDS: ReadonlySet<SelectionAiActionId> = new Set([
  'view_source',
]);

export const isInlineInsightActionId = (
  actionId: SelectionAiActionId
): actionId is SelectionInsightActionId => INLINE_INSIGHT_ACTION_IDS.has(actionId);
